
// This file contains the calculations for the Tag report


// This function returns the most recent responses for each primary factor
export const get_most_recent = (responses) => {
    let tracker = {};
    return {
      date: null,
      responses: responses
        .filter((i) => {
          if (!(i.name in tracker)) {
            tracker[i.name] = i.date;
          }
  
          return i.date === tracker[i.name];
        })
        .map((i) => i.responses)
        .flat()
        .map((i) => i.response)
        .filter((f) => !Array.isArray(f)),
    };
  };

  export const get_last_responses = (responses) => {
    let tracker = {};
  
    return responses
      .filter((i) => {
        if (!(i.name in tracker)) {
          tracker[i.name] = [i.date];
        } else if (tracker[i.name].length === 1) {
          tracker[i.name].push(i.date);
        }
  
        if (tracker[i.name].length === 2) {
          return i.date == tracker[i.name][1];
        }
        return false;
      })
      .map((i) => i.responses)
      .flat()
      .map((i) => i.response)
      .filter((f) => !Array.isArray(f));
  };



export function filter_data(data, filters) {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  if (!filters || !Array.isArray(filters) || filters.length === 0) {
    return data;
  }


  // Separate filters into categories ONCE (outside the loop)
  const categoryFilters = filters.filter(
    (filter) => !filter.isSubfactor && filter.type_of !== "factor" && filter.type_of !== "outcome"
  );

  const subfactorFilters = filters.filter((filter) => filter.isSubfactor);
  const factorFilters = filters.filter(
    (filter) => filter.type_of === "factor"
  );

  const outcomeFilters = filters.filter(
    (filter) => filter.type_of === "outcome"
  );


  return data.reduce((filteredData, response, index) => {
    // Extract response IDs and subfactor IDs from categories
    const responseIds = response?.categories?.map((cat) => cat.response) || [];

    const subfactorIds = (response?.categories
      ?.filter((cat) => cat?.priority === "primary" && cat?.subfactor)
      ?.map((cat) => cat.subfactor) || [])
      .concat(responseIds);

    // Group category filters by parent
    const groupedCategoryFilters = categoryFilters.reduce((acc, filter) => {
      if (!acc[filter.parent]) acc[filter.parent] = [];
      acc[filter.parent].push(filter);
      return acc;
    }, {});

    // Check category filters
    for (let parent in groupedCategoryFilters) {
      const requiredFilters = groupedCategoryFilters[parent];
      const hasMatchingFilter = requiredFilters.some((filter) => {
        return responseIds.includes(filter.id);
      });

      if (!hasMatchingFilter) {
        return filteredData; // Skip this response
      }
    }

    // Check subfactor filters
    if (subfactorFilters.length > 0) {
      const hasMatchingSubfactor = subfactorFilters.some((filter) => {
        return subfactorIds.includes(filter.id);
      });

      if (!hasMatchingSubfactor) {
        return filteredData; // Skip this response
      }
    }

    // Initialize filtered questions and feedback with original data
    // Ensure they're always arrays
    let filteredQuestions = Array.isArray(response.questions) ? response.questions : [];
    let filteredFeedback = Array.isArray(response.feedback) ? response.feedback : [];
    let filteredEmployeeOutcomes = response.employee_outcomes;
    let filteredFeedbackBuilder = response.feedback_builder;

    // Apply factor filters if any
    if (factorFilters.length > 0) {
      // Filter questions - keep questions that match any selected factor
      if (Array.isArray(response.questions) && response.questions.length > 0) {
        filteredQuestions = response.questions.filter((q) => {
          const matches = factorFilters.some(
            (filter) => {
              // Try multiple ways to match: q.dimension or q.id might be dimension index
              // The question's factor property should match filter.id (factor index)
              const dimensionMatch = q.dimension === filter.dimensionIndex || q.id === filter.dimensionIndex;
              const factorMatch = q.factor === filter.id;
              const match = dimensionMatch && factorMatch;

              return match;
            }
          );
          return matches;
        });
      } else {
        filteredQuestions = [];
      }

      // Filter feedback - keep feedback that match any selected factor
      if (Array.isArray(response.feedback) && response.feedback.length > 0) {
        filteredFeedback = response.feedback.filter((f) => {
          const matches = factorFilters.some(
            (filter) => {
              // Try multiple ways to match: f.dimension or f.id might be dimension index
              const dimensionMatch = f.dimension === filter.dimensionIndex || f.id === filter.dimensionIndex;
              const factorMatch = f.factor === filter.id;
              const match = dimensionMatch && factorMatch;
              return match;
            }
          );
          return matches;
        });
      } else {
        filteredFeedback = [];
      }

      // Keep the response if it has matching questions or feedback
      // OR if it has other data types (outcomes, comments, etc.) that should be preserved
      const hasMatchingFactorData =
        filteredQuestions.length > 0 || filteredFeedback.length > 0;

      const hasOtherRelevantData =
        (response.employee_outcomes && Array.isArray(response.employee_outcomes) && response.employee_outcomes.length > 0) ||
        (response.feedback_builder?.responses && Array.isArray(response.feedback_builder.responses) && response.feedback_builder.responses.length > 0) ||
        (response.comments && Array.isArray(response.comments) && response.comments.length > 0);

      // Only skip if there's no matching factor data AND no other relevant data
      if (!hasMatchingFactorData && !hasOtherRelevantData) {
        return filteredData; // Skip this response
      }
    }

    // Apply outcome filters if any
    if (outcomeFilters.length > 0) {
      let hasMatchingOutcome = false;

      // Check employee_outcomes
      if (response.employee_outcomes?.length > 0) {
        hasMatchingOutcome = response.employee_outcomes.some((eo) => {
          return eo.responses?.some((resp) => {
            const matches = outcomeFilters.some((filter) => {
              const match = Number(resp.question_id) === Number(filter.id);
              
              return match;
            });
            
            return matches;
          });
        });
      }

      // Check feedback_builder for outcomes
      if (
        !hasMatchingOutcome &&
        response.feedback_builder?.responses?.length > 0
      ) {
        hasMatchingOutcome = response.feedback_builder.responses.some(
          (resp) => {
            const matches = outcomeFilters.some((filter) => {
              const match = Number(resp.question_id) === Number(filter.id);
              
              return match;
            });
            
            return matches;
          }
        );
      }

      // Filter outcome-related data if outcome filters are applied
      if (hasMatchingOutcome) {
        // Filter employee_outcomes to only include matching outcomes
        if (response.employee_outcomes?.length > 0) {
          filteredEmployeeOutcomes = response.employee_outcomes.map((eo) => ({
            ...eo,
            responses: eo.responses?.filter((resp) => {
              return outcomeFilters.some((filter) => {
                return Number(resp.question_id) === Number(filter.id);
              });
            }) || [],
          })).filter((eo) => eo.responses.length > 0);
        }

        // Filter feedback_builder to only include matching outcomes
        if (response.feedback_builder?.responses?.length > 0) {
          filteredFeedbackBuilder = {
            ...response.feedback_builder,
            responses: response.feedback_builder.responses.filter((resp) => {
              return outcomeFilters.some((filter) => {
                return Number(resp.question_id) === Number(filter.id);
              });
            }),
          };
        }
      } else {
        // If no matching outcomes, clear outcome data
        filteredEmployeeOutcomes = [];
        filteredFeedbackBuilder = response.feedback_builder ? {
          ...response.feedback_builder,
          responses: [],
        } : undefined;
      }

      // If no matching outcomes AND no factor data, check if we should keep the response
      if (!hasMatchingOutcome && filteredQuestions.length === 0 && filteredFeedback.length === 0) {
        // Only skip if there's no other relevant data
        const hasOtherRelevantData =
          (response.comments && Array.isArray(response.comments) && response.comments.length > 0);
        if (!hasOtherRelevantData) {
          return filteredData; // Skip this response
        }
      }
    }

    // Include the response with filtered questions, feedback, and outcomes
    filteredData.push({
      ...response,
      questions: filteredQuestions,
      feedback: filteredFeedback,
      employee_outcomes: outcomeFilters.length > 0 ? filteredEmployeeOutcomes : response.employee_outcomes,
      feedback_builder: outcomeFilters.length > 0 ? filteredFeedbackBuilder : response.feedback_builder,
    });

    return filteredData;
  }, []);
}

  export const get_overall_average = (data) => {
    let totalSum = 0;
    let totalCount = 0;

    data.forEach((item) => {
      item.questions.forEach((question) => {
        if (typeof question.response === "number") {
          totalSum += question.response;
          totalCount++;
        }
      });
    });

    return totalCount ? totalSum / totalCount : 0; // Avoid division by zero
  };

  const sum_array = (arr) => {
    if (!arr) {
      return 0;
    }

    let sum = 0;
    arr.forEach((item) => {
      sum += item;
    });
    return sum;
  };

  export const get_hr_flags = (data) => {
    let flags = 0;
    let feedback_quality = 0;
    let totalCount = 0;

    data.forEach((item) => {
      item?.ml_feedback?.feedback?.forEach((question) => {
        flags += sum_array(question?.conversation_analysis?.hr_flag);

        question?.conversation_analysis?.quality.forEach((quality) => {
          feedback_quality += quality;
          totalCount++;
        });
      });
    });

    return { flags, feedback_quality: feedback_quality / totalCount };
  };

  const get_factor_average = (data, factor, dimension) => {
    let totalSum = 0;
    let totalCount = 0;

    data.forEach((item) => {
      item.questions.forEach((question) => {
        if (question.factor === factor && question.id === dimension) {
          totalSum += question.response;
          totalCount++;
        }
      });
    });

    return totalCount ? totalSum / totalCount : 0; // Avoid division by zero
  };

  const get_item_scores = (data, index, dimension, factor) => {
    let scores = [];
    factor.questions.forEach((item, question) => {
      let totalSum = 0;
      let totalCount = 0;
      data.forEach((response) => {
        response.questions.forEach((q) => {
          if (q.factor === index && q.id === dimension && q.q === question) {
            totalSum += q.response;
            totalCount++;
          }
        });
      });
      scores.push({
        question: item.q,
        score: totalCount ? totalSum / totalCount : 0,
      });
    });
    return scores;
  };

const get_pillar_standard = (standards,index,dimension) => {
   return standards?.response.pillars.find(f=>f.id==dimension && f.factor==index)
}

export const get_scores_per_pillar = (data,survey,org_data,last_data,standards) =>{
    let scores = []
    survey.questions.dimensions.map((item,dimension)=>{
        item.factors.map((factor,index)=>{
           scores.push({name:factor.title,
                        score:get_factor_average(data,index,dimension),
                        item_scores:get_item_scores(data,index,dimension,factor),
                        dimension,
                        factor:index,
                        last_score:get_factor_average(last_data,index,dimension),
                        pillar_standard:get_pillar_standard(standards,index,dimension),
                        org_score:get_factor_average(org_data,index,dimension)})

        })
    })
    return scores
}

export const getStructuredFeedback = (data) => {
    let feedback = []
    data?.map((item)=>{
        item.feedback.map((f)=>{
           feedback.push(f)
        })
    })

    return feedback
}